import { useState, useEffect, useCallback } from 'react';
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  SignInOutput,
  AuthUser,
  confirmSignUp,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

type ProfileType = 'musician' | 'venue';

/**
 * Custom hook to manage authentication state
 * Provides a single instance of the authentication service
 * @returns The useAuth hook
 */
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<SignInOutput | AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState<string>('');
  const [pendingPassword, setPendingPassword] = useState<string>('');

  const clearError = () => setError(null);

  const checkAuthState = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      await fetchAuthSession(); // Verifies session is valid, no need to store the result if not used
      setUser(currentUser);
      setIsAuthenticated(true);
      setIsLoading(false);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  const handleSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const signInResult = await signIn({ username: email, password });
      setUser(signInResult);
      setIsAuthenticated(true);
      await checkAuthState();
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred during sign in';
      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (
    email: string,
    password: string,
    address: string,
    preferredUsername: string,
    profileType: ProfileType
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            address,
            preferred_username: preferredUsername,
            'custom:profileType': profileType,
          },
        },
      });
      setNeedsConfirmation(true);
      setConfirmationEmail(email);
      setPendingPassword(password);
      return { success: true, needsConfirmation: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred during sign up';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred during sign out';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async (email: string, confirmationCode: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await confirmSignUp({
        username: email,
        confirmationCode,
      });
      setNeedsConfirmation(false);
      const signInResult = await handleSignIn(email, pendingPassword);
      setPendingPassword('');
      return signInResult;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred during confirmation';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await resetPassword({ username: email });
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred during password reset';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (
    email: string,
    confirmationCode: string,
    newPassword: string
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      });
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during password reset confirmation';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    confirmationEmail,
    error,
    isAuthenticated,
    isLoading,
    needsConfirmation,
    user,
    // Handlers
    clearError,
    confirmSignUp: handleConfirmSignUp,
    forgotPassword: handleForgotPassword,
    forgotPasswordSubmit: handleForgotPasswordSubmit,
    signIn: handleSignIn,
    signOut: handleSignOut,
    signUp: handleSignUp,
  };
};
